/**
 * Captures UTM query parameters on landing, persists them in localStorage
 * across page views (90 day expiry, manually enforced), and populates the
 * hidden utm_* inputs (see UtmFieldsMixin in forms.py) on every lead form
 * on the page so Salesforce leads retain original ad attribution no matter
 * which page the visitor submits from.
 *
 * Also captures the first-touch referring site (document.referrer) once per
 * visitor and populates a hidden `referrer` input the same way, so Salesforce
 * can see where a lead originally came from even without UTM params.
 *
 * localStorage (not a cookie) is used deliberately: it's never sent to the
 * server (we only ever read it client-side), and it isn't subject to
 * Safari ITP's 7-day cap on JS-set (document.cookie) cookies.
 *
 * Separately, a locked first-touch record (landing URL, UTMs, click IDs) is stored once per visitor for 90 days and
 * populates the first_utm_*, gclid, landing_page_url and meta_click_id hidden inputs.
 */

const UTM_STORAGE_KEY = "berkshomes_utm_params";
const UTM_TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 days
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

const REFERRER_STORAGE_KEY = "berkshomes_referrer";
const REFERRER_FIELD = "referrer";
const OWN_HOSTNAMES = ["berkshomes.com", "www.berkshomes.com"];

function captureUtmFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const values = {};
    UTM_KEYS.forEach((key) => {
        if (params.has(key)) {
            values[key] = params.get(key);
        }
    });

    if (!Object.keys(values).length) {
        return null;
    }

    storeUtmParams(values);
    return values;
}

function storeUtmParams(values) {
    try {
        window.localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify({
            values,
            expires: Date.now() + UTM_TTL_MS,
        }));
    } catch (e) {
        // localStorage unavailable (private mode, quota, etc.) - fail silently.
    }
}

function readStoredUtmParams() {
    try {
        const raw = window.localStorage.getItem(UTM_STORAGE_KEY);
        if (!raw) {
            return null;
        }

        const stored = JSON.parse(raw);
        if (!stored.values || Date.now() > stored.expires) {
            window.localStorage.removeItem(UTM_STORAGE_KEY);
            return null;
        }

        return stored.values;
    } catch (e) {
        return null;
    }
}

function resolveUtmParams() {
    return captureUtmFromUrl() || readStoredUtmParams() || {};
}

function populateUtmFields(values) {
    UTM_KEYS.forEach((key) => {
        const value = values[key] || "";
        document.querySelectorAll(`input[name="${key}"]`).forEach((input) => {
            input.value = value;
        });
    });
}

// First-touch referrer: captured once and never overwritten by later visits,
// unlike UTM params, which refresh whenever new ones appear in the URL.
function resolveReferrer() {
    const stored = window.localStorage.getItem(REFERRER_STORAGE_KEY);
    if (stored !== null) {
        return stored;
    }

    let referrer = "";
    try {
        const referrerHost = document.referrer && new URL(document.referrer).hostname;
        if (referrerHost && !OWN_HOSTNAMES.includes(referrerHost)) {
            referrer = referrerHost;
        }
    } catch (e) {
        // Malformed document.referrer - treat as a direct visit.
    }

    try {
        window.localStorage.setItem(REFERRER_STORAGE_KEY, referrer);
    } catch (e) {
        // localStorage unavailable (private mode, quota, etc.) - fail silently.
    }

    return referrer;
}

function populateReferrerField(value) {
    document.querySelectorAll(`input[name="${REFERRER_FIELD}"]`).forEach((input) => {
        input.value = value;
    });
}

const FIRST_TOUCH_STORAGE_KEY = "berkshomes_first_touch";
const CLICK_ID_KEYS = ["gclid", "gbraid", "wbraid", "fbclid"];

function readFirstTouch() {
    try {
        const raw = window.localStorage.getItem(FIRST_TOUCH_STORAGE_KEY);
        if (!raw) {
            return null;
        }

        const stored = JSON.parse(raw);
        if (!stored.values || Date.now() > stored.expires) {
            window.localStorage.removeItem(FIRST_TOUCH_STORAGE_KEY);
            return null;
        }

        return stored.values;
    } catch (e) {
        return null;
    }
}

// Locked for the TTL: later visits never overwrite it, even with new campaign params.
function resolveFirstTouch() {
    const existing = readFirstTouch();
    if (existing) {
        return existing;
    }

    const params = new URLSearchParams(window.location.search);
    const values = {landing_page_url: window.location.href};
    [...UTM_KEYS, ...CLICK_ID_KEYS].forEach((key) => {
        if (params.has(key)) {
            values[key] = params.get(key);
        }
    });

    try {
        window.localStorage.setItem(FIRST_TOUCH_STORAGE_KEY, JSON.stringify({
            values,
            expires: Date.now() + UTM_TTL_MS,
        }));
    } catch (e) {
        // localStorage unavailable (private mode, quota, etc.) - fail silently.
    }

    return values;
}

function readFbcCookie() {
    const match = document.cookie.match(/(?:^|;\s*)_fbc=([^;]+)/);
    return match ? decodeURIComponent(match[1]) : "";
}

function resolveMetaClickId(firstTouch) {
    return readFbcCookie() || (firstTouch.fbclid ? `fb.1.${Date.now()}.${firstTouch.fbclid}` : "");
}

function populateFirstTouchFields(firstTouch) {
    const fieldValues = {
        gclid: firstTouch.gclid || firstTouch.gbraid || firstTouch.wbraid || "",
        landing_page_url: firstTouch.landing_page_url || "",
        meta_click_id: resolveMetaClickId(firstTouch),
    };
    UTM_KEYS.forEach((key) => {
        fieldValues[`first_${key}`] = firstTouch[key] || "";
    });

    Object.entries(fieldValues).forEach(([name, value]) => {
        document.querySelectorAll(`input[name="${name}"]`).forEach((input) => {
            input.value = value;
        });
    });
}

document.addEventListener("DOMContentLoaded", () => {
    populateUtmFields(resolveUtmParams());
    populateReferrerField(resolveReferrer());
    populateFirstTouchFields(resolveFirstTouch());
});
